import React, { useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import Layout from '../Layout'
import FormField from '../../../Components/FormField'
import Modal from '../../../Components/Modal'
import Button from '../../../Components/Button'
import {
  ROLE_COLORS,
  ROLE_DESCRIPTIONS,
  EXPIRY_OPTIONS,
  formatExpiryCountdown,
  capitalize
} from '../../../utils/formatters'

export default function Index({ account, accounts, navigation, memberships, invites, roles, invite_base_url }) {
  const { errors } = usePage().props
  const [showInviteForm, setShowInviteForm] = useState(false)
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'member', expires_in_days: '7' })

  // Modal state
  const [editingMembership, setEditingMembership] = useState(null)
  const [editingInvite, setEditingInvite] = useState(null)
  const [membershipForm, setMembershipForm] = useState({ role: '' })
  const [editInviteForm, setEditInviteForm] = useState({ role: '', expires_in_days: '7' })

  // Copy state
  const [copiedInviteId, setCopiedInviteId] = useState(null)

  const handleInviteChange = (e) => setInviteForm(prev => ({ ...prev, [e.target.name]: e.target.value }))

  const handleInviteSubmit = (e) => {
    e.preventDefault()
    router.post('/manage/invites', { invite: inviteForm }, {
      onSuccess: () => {
        setShowInviteForm(false)
        setInviteForm({ email: '', role: 'member', expires_in_days: '7' })
      }
    })
  }

  // Membership actions
  const openEditMembership = (membership) => {
    setMembershipForm({ role: membership.role })
    setEditingMembership(membership)
  }

  const handleUpdateMembership = (e) => {
    e.preventDefault()
    router.patch(`/manage/memberships/${editingMembership.id}`, { membership: membershipForm }, {
      onSuccess: () => setEditingMembership(null)
    })
  }

  const handleRemoveMember = (membership) => {
    if (confirm(`Remove ${membership.user.email} from the team?`)) {
      router.delete(`/manage/memberships/${membership.id}`)
    }
  }

  // Invite actions
  const openEditInvite = (invite) => {
    setEditInviteForm({ role: invite.role, expires_in_days: '7' })
    setEditingInvite(invite)
  }

  const handleUpdateInvite = (e) => {
    e.preventDefault()
    router.patch(`/manage/invites/${editingInvite.id}`, { invite: editInviteForm }, {
      onSuccess: () => setEditingInvite(null)
    })
  }

  const handleResendInvite = (invite) => {
    router.post(`/manage/invites/${invite.id}/resend`)
  }

  const handleCancelInvite = (invite) => {
    if (confirm(`Cancel invite for ${invite.email}?`)) {
      router.delete(`/manage/invites/${invite.id}`)
    }
  }

  const copyInviteLink = async (invite) => {
    const url = `${invite_base_url}${invite.token}`
    try {
      await navigator.clipboard.writeText(url)
      setCopiedInviteId(invite.id)
      setTimeout(() => setCopiedInviteId(null), 2000)
    } catch (err) {
      // Fallback for older browsers
      const input = document.createElement('input')
      input.value = url
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      document.body.removeChild(input)
      setCopiedInviteId(invite.id)
      setTimeout(() => setCopiedInviteId(null), 2000)
    }
  }

  const roleOptions = roles.map(role => ({
    value: role,
    label: capitalize(role)
  }))

  return (
    <Layout account={account} accounts={accounts} navigation={navigation} title="Team">
      <div className="mb-6 flex justify-between items-center">
        <p className="text-sm text-gray-600">{memberships.length} team member{memberships.length !== 1 ? 's' : ''}</p>
        <Button onClick={() => setShowInviteForm(!showInviteForm)}>
          {showInviteForm ? 'Cancel' : 'Invite Team Member'}
        </Button>
      </div>

      {/* Invite Form */}
      {showInviteForm && (
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Invite Team Member</h3>
          <form onSubmit={handleInviteSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                label="Email"
                name="email"
                type="email"
                value={inviteForm.email}
                onChange={handleInviteChange}
                required
                error={errors?.email}
                placeholder="colleague@example.com"
              />
              <FormField
                label="Role"
                name="role"
                type="select"
                value={inviteForm.role}
                onChange={handleInviteChange}
                options={roleOptions}
                required
                hint={ROLE_DESCRIPTIONS[inviteForm.role]}
              />
              <FormField
                label="Expires"
                name="expires_in_days"
                type="select"
                value={inviteForm.expires_in_days}
                onChange={handleInviteChange}
                options={EXPIRY_OPTIONS}
                required
              />
            </div>
            <div className="flex justify-end">
              <Button type="submit">Send Invite</Button>
            </div>
          </form>
        </div>
      )}

      {/* Pending Invites */}
      {invites.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Pending Invites</h3>
          <div className="bg-white shadow rounded-lg divide-y divide-gray-200">
            {invites.map((invite) => (
              <div key={invite.id} className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-medium text-gray-900 truncate">{invite.email}</p>
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${ROLE_COLORS[invite.role] || 'bg-gray-100 text-gray-800'}`}>
                        {capitalize(invite.role)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">{formatExpiryCountdown(invite.expires_at)}</p>

                    {/* Invite Link */}
                    <div className="mt-2 flex items-center gap-2">
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-600 truncate max-w-xs">
                        {invite_base_url}{invite.token}
                      </code>
                      <button
                        onClick={() => copyInviteLink(invite)}
                        className="text-xs text-sky-600 hover:text-sky-500 whitespace-nowrap"
                      >
                        {copiedInviteId === invite.id ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 ml-4">
                    <Button link variant="muted" onClick={() => openEditInvite(invite)}>Edit</Button>
                    <Button link onClick={() => handleResendInvite(invite)}>Resend</Button>
                    <Button link variant="danger" onClick={() => handleCancelInvite(invite)}>Cancel</Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Team Members */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <ul className="divide-y divide-gray-200">
          {memberships.map((membership) => (
            <li key={membership.id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50">
              <div>
                <p className="text-sm font-medium text-gray-900">{membership.user.email}</p>
                <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${ROLE_COLORS[membership.role] || 'bg-gray-100 text-gray-800'}`}>
                  {capitalize(membership.role)}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <Button link variant="muted" onClick={() => openEditMembership(membership)}>Edit</Button>
                <Button link variant="danger" onClick={() => handleRemoveMember(membership)}>Remove</Button>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Edit Membership Modal */}
      {editingMembership && (
        <Modal onClose={() => setEditingMembership(null)} title="Edit Team Member">
          <form onSubmit={handleUpdateMembership} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <p className="text-sm text-gray-900">{editingMembership.user.email}</p>
            </div>
            <FormField
              label="Role"
              name="role"
              type="select"
              value={membershipForm.role}
              onChange={(e) => setMembershipForm({ role: e.target.value })}
              options={roleOptions}
              required
              hint={ROLE_DESCRIPTIONS[membershipForm.role]}
            />
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="secondary" type="button" onClick={() => setEditingMembership(null)}>
                Cancel
              </Button>
              <Button type="submit">Save Changes</Button>
            </div>
          </form>
        </Modal>
      )}

      {/* Edit Invite Modal */}
      {editingInvite && (
        <Modal onClose={() => setEditingInvite(null)} title="Edit Invite">
          <form onSubmit={handleUpdateInvite} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <p className="text-sm text-gray-900">{editingInvite.email}</p>
            </div>
            <FormField
              label="Role"
              name="role"
              type="select"
              value={editInviteForm.role}
              onChange={(e) => setEditInviteForm(prev => ({ ...prev, role: e.target.value }))}
              options={roleOptions}
              required
              hint={ROLE_DESCRIPTIONS[editInviteForm.role]}
            />
            <FormField
              label="Extend Expiry"
              name="expires_in_days"
              type="select"
              value={editInviteForm.expires_in_days}
              onChange={(e) => setEditInviteForm(prev => ({ ...prev, expires_in_days: e.target.value }))}
              options={EXPIRY_OPTIONS}
              hint="Sets new expiry from now"
            />
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="secondary" type="button" onClick={() => setEditingInvite(null)}>
                Cancel
              </Button>
              <Button type="submit">Save Changes</Button>
            </div>
          </form>
        </Modal>
      )}
    </Layout>
  )
}
